import { useCallback, useRef, useState } from 'react'
import type { TextOnPathToolbarRef } from '@/components/app/editor/tools/text-on-path-toolbar'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'
import { type TextOnPathDraft, clampStartDistance } from '@/lib/shapes/text-on-path-session'

/**
 * Text on a Path mode state: the draft being edited beside its shape, the shape snapshot that
 * positions the bar, the measured path length, and the draft edits the bar and the on-canvas dot
 * raise. `exitTextOnPathMode` discards the draft; committing lives in `use-text-on-path-session`.
 */
export function useTextOnPathState() {
  // Text on a Path mode (WMPCM-9514) — entered from the object/photo context menus, the Object
  // menu, or the left panel. Same shape as crop/node-edit (`use-crop-state`,
  // `use-node-edit-state`): one shape at a time, its own floating bar, and a draft that lives
  // BESIDE the shape rather than on it (nothing about text on a path is persisted until
  // WMPCM-9542).
  const [textOnPathDraft, setTextOnPathDraft] = useState<TextOnPathDraft | null>(null)
  const [textOnPathShapeData, setTextOnPathShapeData] = useState<ShapeData | null>(null)
  // Reported by ShapeRenderer off `getTotalLength()` of the previewed path; bounds the bar's
  // Position control. 0 until the path has been measured (and always 0 in jsdom).
  const [textOnPathLength, setTextOnPathLength] = useState(0)
  const textOnPathShapeIdRef = useRef<string | null>(null)
  const textOnPathDraftRef = useRef<TextOnPathDraft | null>(null)
  // Escape asks the bar to cancel an in-progress grip drag before it exits the mode.
  const textOnPathToolbarRef = useRef<TextOnPathToolbarRef>(null)

  textOnPathShapeIdRef.current = textOnPathDraft?.shapeId ?? null
  // Read during render, not in an effect (WMPCM-6867): the dismiss paths are event callbacks that
  // must see the draft as it is right now, not as it was when they were created.
  textOnPathDraftRef.current = textOnPathDraft
  const isTextOnPathMode = textOnPathDraft != null

  /** Close the bar and throw the draft away. This is Escape. */
  const exitTextOnPathMode = useCallback(() => {
    // Null the ref synchronously, not just the state. `onSelectionChange` is a layout effect and
    // can fire again before the re-render that would re-sync this ref, and a stale draft there
    // would run the commit a second time.
    textOnPathDraftRef.current = null
    setTextOnPathDraft(null)
    setTextOnPathShapeData(null)
    setTextOnPathLength(0)
  }, [])

  const handleTextOnPathTextChange = useCallback((text: string) => {
    setTextOnPathDraft((prev) => (prev == null ? prev : { ...prev, text }))
  }, [])

  const handleTextOnPathStartDistanceChange = useCallback((startDistance: number) => {
    setTextOnPathDraft((prev) => (prev == null ? prev : { ...prev, startDistance }))
  }, [])

  // Every Format Text control (WMPCM-9522) lands here. One patch callback rather than six
  // field-specific ones — they all do the same thing to the same draft object.
  const handleTextOnPathConfigChange = useCallback((patch: Partial<TextOnPathConfig>) => {
    setTextOnPathDraft((prev) => (prev == null ? prev : { ...prev, ...patch }))
  }, [])

  // Raised by ShapeRenderer's red-dot drag, which already projected the pointer back onto the
  // path; the shape id is passed through so a stale event from a previous target can't land.
  const handleTextOnPathDotDrag = useCallback((shapeId: string, startDistance: number) => {
    setTextOnPathDraft((prev) =>
      prev == null || prev.shapeId !== shapeId ? prev : { ...prev, startDistance }
    )
  }, [])

  const handleTextOnPathLengthChange = useCallback((shapeId: string, length: number) => {
    setTextOnPathLength((prev) => (prev === length ? prev : length))
    // Re-clamp: the previewed path can be shorter than the start position the draft is holding
    // (e.g. the object was resized while the bar was open), which would otherwise pin the text
    // and the dot to different places.
    setTextOnPathDraft((prev) => {
      if (prev == null || prev.shapeId !== shapeId) return prev
      // A length of 0 means "not measured yet" (the path's `d` is still resolving, or no SVG
      // geometry), not "the path is 0pt long". Clamping into `[0, 0]` would zero the stored
      // start position, and the next dismiss would commit that 0 (WMPCM-11097).
      if (!(length > 0)) return prev
      const clamped = clampStartDistance(prev.startDistance, length)

      return clamped === prev.startDistance ? prev : { ...prev, startDistance: clamped }
    })
  }, [])

  return {
    textOnPathDraft,
    setTextOnPathDraft,
    textOnPathShapeData,
    setTextOnPathShapeData,
    textOnPathLength,
    setTextOnPathLength,
    textOnPathShapeIdRef,
    textOnPathDraftRef,
    textOnPathToolbarRef,
    isTextOnPathMode,
    exitTextOnPathMode,
    handleTextOnPathTextChange,
    handleTextOnPathStartDistanceChange,
    handleTextOnPathConfigChange,
    handleTextOnPathDotDrag,
    handleTextOnPathLengthChange,
  }
}
